import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FR } from "@/data/fr";
import { GoldButton, ComplianceBadge } from "@/components/ds";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { setPageMeta, injectJsonLd } from "@/lib/seo";

const French = () => {
  useEffect(() => {
    const prev = document.documentElement.lang;
    document.documentElement.lang = "fr";
    setPageMeta({ title: FR.meta.title, description: FR.meta.description, path: "/fr" });
    injectJsonLd("fr-jsonld", {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: FR.meta.title,
      description: FR.meta.description,
      inLanguage: "fr",
      isPartOf: { "@type": "WebSite", name: "Zorem Commodities", url: "https://zoremcommodities.com" },
    });
    return () => {
      document.documentElement.lang = prev;
      document.getElementById("fr-jsonld")?.remove();
    };
  }, []);

  return (
    <div data-testid="fr-page" lang="fr" className="bg-[#0A0A0B] min-h-screen">
      <div className="relative pt-44 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/assets/hero-bars.webp" alt="Lingots d'or empilés dans un coffre" className="w-full h-full object-cover object-right opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/80 to-[#0A0A0B]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <Eyebrow>{FR.hero.eyebrow}</Eyebrow>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium uppercase leading-[1.12] tracking-wide">
              {FR.hero.lines.map((l, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${i === 1 ? "gold-text" : "text-white"}`}
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1.05, delay: 0.25 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {l}
                  </motion.span>
                </span>
              ))}
            </h1>
            <p className="mt-7 text-base md:text-lg text-zinc-400 leading-relaxed max-w-xl">{FR.hero.sub}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <GoldButton to="/mandate" testid="fr-cta-mandate">{FR.hero.ctaPrimary}</GoldButton>
              <GoldButton href="#fr-portefeuille" variant="ghost" testid="fr-cta-portfolio">{FR.hero.ctaSecondary}</GoldButton>
            </div>
          </div>
        </div>
      </div>

      <div className="border-y border-[#C9A227]/15 bg-[#0A0A0B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#C9A227]/15" data-testid="fr-stats">
          {FR.stats.map((s) => (
            <div key={s.label} className="bg-[#0A0A0B] p-7">
              <div className="font-display text-3xl text-white">{s.value}</div>
              <div className="mt-2 text-[12px] uppercase tracking-[0.18em] text-[#D4AF37]">{s.label}</div>
              <div className="mt-2 text-sm text-zinc-400">{s.sub}</div>
            </div>
          ))}
        </div>
      </div>

      <section className="py-24 max-w-4xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Eyebrow>{FR.axes.eyebrow}</Eyebrow>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-wide text-white leading-snug">
            Dans ce métier, beaucoup parlent. <span className="gold-text">Nous exécutons.</span>
          </h2>
          {FR.axes.paras.map((p, i) => <p key={i} className="mt-6 text-base text-zinc-400 leading-relaxed">{p}</p>)}
        </Reveal>
      </section>

      <section id="fr-portefeuille" className="py-24 bg-[#121215] hairline-t hairline-b">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>{FR.portfolio.eyebrow}</Eyebrow>
            <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-wide text-white">{FR.portfolio.title}</h2>
            <p className="mt-5 text-base text-zinc-400 max-w-2xl leading-relaxed">{FR.portfolio.intro}</p>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 gap-px bg-[#C9A227]/15 card-line">
            {FR.portfolio.groups.map((g) => (
              <div key={g.name} className="bg-[#0A0A0B] p-9">
                <h3 className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#C9A227] mb-5">{g.name}</h3>
                <ul className="space-y-3 text-sm text-zinc-400">
                  {g.items.map((it) => (
                    <li key={it} className="flex gap-3"><span className="w-1.5 h-1.5 rotate-45 bg-[#C9A227] shrink-0 mt-1.5" aria-hidden="true" />{it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Eyebrow>{FR.compliance.eyebrow}</Eyebrow>
          <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-wide text-white max-w-2xl">{FR.compliance.title}</h2>
          {FR.compliance.paras.map((p, i) => <p key={i} className="mt-5 text-base text-zinc-400 max-w-2xl leading-relaxed">{p}</p>)}
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fr-badges">
          {FR.compliance.badges.map((b) => <ComplianceBadge key={b} label={b} testid={`fr-badge-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />)}
        </div>
      </section>

      <section className="py-24 bg-[#121215] hairline-t hairline-b">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <Eyebrow>{FR.pathway.eyebrow}</Eyebrow>
            <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-wide text-white">{FR.pathway.title}</h2>
            <ol className="mt-8 space-y-4">
              {FR.pathway.steps.map((s, i) => (
                <li key={i} className="flex gap-4 text-sm text-zinc-400">
                  <span className="font-mono2 text-[#C9A227] shrink-0">{String(i + 1).padStart(2, "0")}</span>{s}
                </li>
              ))}
            </ol>
            <Link to="/process" data-testid="fr-link-process" className="inline-block mt-6 font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#C9A227] hover:text-[#E8C85A] transition-colors">
              Voir le parcours complet →
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>{FR.settlement.eyebrow}</Eyebrow>
            <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-wide text-white">{FR.settlement.title}</h2>
            <div className="mt-8 space-y-5">
              {FR.settlement.items.map((it) => (
                <div key={it.k} className="card-line bg-[#0A0A0B] p-5">
                  <div className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">{it.k}</div>
                  <p className="mt-2 text-sm text-zinc-400">{it.v}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 max-w-4xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Eyebrow>{FR.leadership.eyebrow}</Eyebrow>
          <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-wide text-white">{FR.leadership.name}</h2>
          <p className="mt-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#C9A227]">{FR.leadership.role}</p>
          <p className="mt-5 text-base text-zinc-400 leading-relaxed">{FR.leadership.text}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-14 card-line bg-[#121215] p-10 text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-wide text-white">{FR.cta.title}</h2>
            <p className="mt-4 text-base text-zinc-400 leading-relaxed max-w-xl mx-auto">{FR.cta.text}</p>
            <div className="mt-8"><GoldButton to="/mandate" testid="fr-cta-bottom">{FR.cta.button}</GoldButton></div>
            <p className="mt-8 font-mono2 text-[11px] tracking-[0.12em] text-zinc-400 uppercase">{FR.cta.address}</p>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default French;
